import { useEffect, useRef } from 'react';
import {
  useApi,
  microsoftAuthApiRef,
  SessionState,
  configApiRef,
} from '@backstage/core-plugin-api';

// localStorage key for cross-tab session sync communication
const SESSION_SYNC_KEY = 'backstage-session-sync';
const SIGNOUT_EVENT_PREFIX = 'signout:';

/**
 * Safely log errors in development mode only
 */
const logError = (context: string, error: unknown): void => {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn(`[SessionSync] ${context}:`, error);
  }
};

/**
 * Utility function to broadcast sign-out to other tabs.
 * Uses localStorage for cross-tab communication to avoid same-tab self-delivery
 * that can occur with BroadcastChannel when using separate channel instances.
 */
export const broadcastSignOutEvent = (): void => {
  // Use localStorage for cross-tab communication
  // This avoids the self-delivery issue that occurs with BroadcastChannel
  // when creating a new channel instance for sending
  try {
    localStorage.setItem(
      SESSION_SYNC_KEY,
      `${SIGNOUT_EVENT_PREFIX}${Date.now()}`,
    );
    // Remove immediately - the storage event fires on other tabs when the value changes
    localStorage.removeItem(SESSION_SYNC_KEY);
  } catch (error) {
    logError('Failed to broadcast sign-out via localStorage', error);
  }
};

/**
 * Hook that synchronizes sign-out events across browser tabs.
 *
 * Context: This addresses the multi-tab session consistency issue described in PR #479.
 * When users have multiple tabs open and sign out in one tab, the other tabs would
 * previously continue showing authenticated UI until manually refreshed. This could
 * lead to confusion and potential security concerns with stale sessions.
 *
 * Problem: When a user has multiple tabs open and signs out in one tab,
 * the other tabs still show the authenticated UI until the user refreshes.
 * This can be confusing and potentially a security concern.
 *
 * Solution:
 * 1. Listen to localStorage changes to detect sign-out events from other tabs
 * 2. Subscribe to Microsoft auth session state to detect sign-out in current tab
 *
 * This hook both listens for sign-out broadcasts AND broadcasts sign-out events
 * when the user signs out via any method (Settings → General or Authentication Providers).
 */
export const useSessionSync = (): void => {
  const microsoftAuth = useApi(microsoftAuthApiRef);
  const configApi = useApi(configApiRef);
  const lastMicrosoftStateRef = useRef<SessionState | null>(null);

  // Get the base URL for redirect (defaults to '/' if not configured)
  const baseUrl = configApi.getOptionalString('app.baseUrl') ?? '/';
  // Extract pathname from base URL for redirect
  // Handle both full URLs (https://example.com/backstage) and path-only values (/backstage)
  const redirectPath = (() => {
    // If it starts with '/', it's already a path - use it directly
    if (baseUrl.startsWith('/')) {
      return baseUrl;
    }
    // Otherwise, try to parse as a full URL
    try {
      return new URL(baseUrl).pathname;
    } catch {
      return '/';
    }
  })();

  // Listen for sign-out broadcasts from other tabs via localStorage
  useEffect(() => {
    // Storage event handler - fires when localStorage changes in another tab
    // Only responds to explicit sign-out messages with the "signout:" prefix
    const handleStorageEvent = (event: StorageEvent) => {
      if (
        event.key === SESSION_SYNC_KEY &&
        event.newValue?.startsWith(SIGNOUT_EVENT_PREFIX)
      ) {
        window.location.href = redirectPath;
      }
    };
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [redirectPath]);

  // Subscribe to Microsoft auth session state to broadcast sign-out events
  useEffect(() => {
    if (!microsoftAuth?.sessionState$) {
      logError(
        'Microsoft Auth API not available',
        'sessionState$ is undefined',
      );
      return undefined;
    }

    const subscription = microsoftAuth.sessionState$().subscribe({
      next: state => {
        const previousState = lastMicrosoftStateRef.current;

        // Only broadcast when transitioning from signed-in to an explicit signed-out state
        // This avoids false positives from intermediate states during token renewal
        if (
          previousState === SessionState.SignedIn &&
          state === SessionState.SignedOut
        ) {
          broadcastSignOutEvent();
        }

        lastMicrosoftStateRef.current = state;
      },
      error: error => {
        logError('Microsoft auth subscription error', error);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [microsoftAuth]);
};
